import { useEffect, useRef, useState } from "react";
import {
  FileUploadManager,
  formatFileSize,
  selectStatusRow,
} from "../services/fileUploadManagerService";
import {
  FileUploadState,
  FileUploadWithProgressProps,
  UploadManagerState,
} from "../types/fileUploadWithProgress";

import axios from "axios";
import { useDropzone } from "react-dropzone";
import { nuclearn_api_url } from "../../App";
import { FileWithPreview } from "../Chat/types/fileTypes";
import { useFileStatusStore } from "../Chat/contexts/FileStatusContext";
import { useQueryClient } from "react-query";

export const useFileUploadWithProgress = (
  props: FileUploadWithProgressProps,
) => {
  const {
    context,
    contextId,
    maxFiles = 10,
    maxFileSize = undefined, // No size restriction by default
    acceptedFileTypes,
    allowMultiple = true,
    uploadConcurrency = "sequential",
    autoStartUpload = true,
    chunkSize,
    interceptFiles,
    onFilesSelected,
    onFilesChange,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onProcessingStart,
    onProcessingProgress,
    onProcessingComplete,
    onError,
    onAllComplete,
    onRemoveFile,
    existingFiles = [],
  } = props;

  // State for temporary validation errors
  const [validationError, setValidationError] = useState<string | null>(null);
  const validationErrorTimeout = useRef<NodeJS.Timeout | null>(null);

  // Dynamic context state for runtime updates (e.g., dialogue creation)
  const [dynamicContextId, setDynamicContextId] = useState<
    string | number | null
  >(contextId);

  // Update dynamic context ID (for ad hoc dialogue creation)
  const updateContextId = (newContextId: string | number) => {
    setDynamicContextId(newContextId);
  };

  // Forget a runtime-assigned context id so uploads fall back to the
  // `contextId` prop (a host resetting its conversation — see
  // ChatPromptInput.uploadResetKey).
  const resetContextId = () => {
    setDynamicContextId(null);
  };

  // Get effective context ID (dynamic takes precedence)
  const getEffectiveContextId = () => {
    return dynamicContextId || contextId;
  };

  // Helper function to get base filename without hash
  // NOTE: Currently unused since we're not syncing from projectData anymore
  // Kept for potential future use or if needed by other parts of the hook
  const getBaseFilename = (filename: string) => {
    // Remove hash patterns like "_59726a99577f45c88a234565253ee129" from filename
    const parts = filename.split(".");
    if (parts.length > 1) {
      const nameWithoutExt = parts.slice(0, -1).join(".");
      const extension = parts[parts.length - 1];
      // Remove hash pattern (underscore followed by 32 hex characters)
      const nameWithoutHash = nameWithoutExt.replace(/_[a-f0-9]{32}$/, "");
      return `${nameWithoutHash}.${extension}`;
    }
    return filename.replace(/_[a-f0-9]{32}$/, "");
  };

  // Suppress unused warning - kept for potential future use
  void getBaseFilename;

  // State management - Start with existing files only
  const [files, setFiles] = useState<Map<string, FileUploadState>>(() => {
    return new Map(existingFiles.map((file) => [file.id, file]));
  });

  // Notify parent whenever files change
  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(Array.from(files.values()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]); // Only watch files, not the callback

  // Track which files were added locally (for deduplication)
  const locallyAddedFiles = useRef<Set<string>>(new Set());

  // Track which existing files have been processed to prevent infinite loops
  const processedExistingFiles = useRef<Set<string>>(new Set());

  // Track files that have been removed during backend sync to prevent re-adding
  const removedDuringSync = useRef<Set<string>>(new Set());

  // Track if we've completed initial mount to distinguish initial restoration from feedback loops
  const hasCompletedInitialMount = useRef<boolean>(false);

  // Synchronization locks and state management
  const syncLock = useRef({
    isProcessingBackendSync: false,
    isDeduplicating: false,
  });

  // Processing mutex to ensure atomic operations
  const processingMutex = useRef(Promise.resolve());

  // Permanent file identity tracking
  const fileIdentityMap = useRef<Map<string, string>>(new Map()); // identity -> fileId
  const permanentlyRemovedFiles = useRef<Set<string>>(new Set()); // permanently removed fileIds

  // Query invalidation debouncing (kept but simplified)
  const queryInvalidationTimeout = useRef<NodeJS.Timeout | null>(null);
  const completedFileIds = useRef<Set<string>>(new Set());

  // Refs
  const managerRef = useRef<FileUploadManager | null>(null);
  const queryClient = useQueryClient();

  // Fetch project data when context is dialogue and we have a dialogue ID
  const dialogueId =
    context === "dialogue" &&
    typeof (dynamicContextId || contextId) === "number"
      ? ((dynamicContextId || contextId) as number)
      : undefined;

  // Mutex wrapper for atomic operations
  const withMutex = async (operation: () => void | Promise<void>) => {
    processingMutex.current = processingMutex.current.then(async () => {
      await operation();
    });
    return processingMutex.current;
  };

  // Get unique file identity for deduplication
  const getFileIdentity = (file: FileUploadState): string => {
    // Prefer stable backend identifiers when available
    const fileLike = file.file as any;
    const sourceDataRecordId =
      file.sourceDataRecordId ?? fileLike?.sourceDataRecordId;
    if (sourceDataRecordId) {
      return `sdr-${sourceDataRecordId}`;
    }

    const datasetId = file.datasetId ?? fileLike?.datasetId;
    if (datasetId) {
      const name = (file.file?.name || "unknown").toLowerCase();
      return `dataset-${datasetId}-${name}`;
    }

    // Fall back to file metadata when backend identifiers are not yet available
    const filename = file.file.name;
    const size = file.file.size;
    const modified = (file.file as any).lastModified || 0;
    return `${filename}-${size}-${modified}`;
  };

  // Check if file can be added (not duplicate, not permanently removed)
  // const canAddFile = useCallback((file: FileUploadState): boolean => {
  //   // Never re-add permanently removed files
  //   if (permanentlyRemovedFiles.current.has(file.id)) {
  //     return false;
  //   }

  //   const identity = getFileIdentity(file);
  //   const existingFileId = fileIdentityMap.current.get(identity);

  //   // If identity exists and points to a different file, it's a duplicate
  //   if (existingFileId && existingFileId !== file.id) {
  //     // Check if the existing file still exists in current state
  //     const existingFileExists = files.has(existingFileId);
  //     if (existingFileExists) {
  //       return false; // Duplicate exists
  //     }
  //     // If existing file was removed, we can use this identity
  //     fileIdentityMap.current.set(identity, file.id);
  //   } else if (!existingFileId) {
  //     // New identity, register it
  //     fileIdentityMap.current.set(identity, file.id);
  //   }

  //   return true;
  // }, [files, getFileIdentity]);

  // Atomic deduplication function
  const atomicDeduplication = () => {
    if (syncLock.current.isDeduplicating) {
      return;
    }

    syncLock.current.isDeduplicating = true;

    try {
      setFiles((prev) => {
        const deduplicatedFiles = new Map<string, FileUploadState>();
        const seenDatasetIds = new Set<number>();
        const seenIdentities = new Set<string>();

        // First pass: Add all backend files (they take priority)
        Array.from(prev.values())
          .filter((f) => !locallyAddedFiles.current.has(f.id))
          .forEach((backendFile) => {
            const identity = getFileIdentity(backendFile);

            if (
              !seenIdentities.has(identity) &&
              !permanentlyRemovedFiles.current.has(backendFile.id)
            ) {
              if (backendFile.datasetId) {
                seenDatasetIds.add(backendFile.datasetId);
              }
              deduplicatedFiles.set(backendFile.id, backendFile);
              seenIdentities.add(identity);
              fileIdentityMap.current.set(identity, backendFile.id);
            }
          });

        // Second pass: Add local files ONLY if no backend equivalent exists
        Array.from(prev.values())
          .filter((f) => locallyAddedFiles.current.has(f.id))
          .forEach((localFile) => {
            if (permanentlyRemovedFiles.current.has(localFile.id)) {
              // Clean up permanently removed file
              if (localFile.preview) {
                URL.revokeObjectURL(localFile.preview);
              }
              locallyAddedFiles.current.delete(localFile.id);
              return;
            }

            // Check if we already have this exact file from backend (by file identity, not just datasetId)
            // For Option A architecture, multiple files can share the same datasetId
            const identity = getFileIdentity(localFile);
            if (!seenIdentities.has(identity)) {
              // This is a unique file - add it
              deduplicatedFiles.set(localFile.id, localFile);
              seenIdentities.add(identity);
              fileIdentityMap.current.set(identity, localFile.id);
            } else {
              // Duplicate file already exists from backend - clean up local copy
              if (localFile.preview) {
                URL.revokeObjectURL(localFile.preview);
              }
              locallyAddedFiles.current.delete(localFile.id);
              fileIdentityMap.current.delete(identity);
            }
          });

        return deduplicatedFiles;
      });
    } finally {
      syncLock.current.isDeduplicating = false;
    }
  };

  // Immediate deduplication trigger (no timeout)
  const triggerDeduplication = () => {
    withMutex(atomicDeduplication);
  };

  const removeLocalDuplicates = (
    filesMap: Map<string, FileUploadState>,
    backendFiles: FileUploadState[],
  ) => {
    if (
      backendFiles.length === 0 ||
      locallyAddedFiles.current.size === 0 ||
      filesMap.size === 0
    ) {
      return { map: filesMap, removed: false } as const;
    }

    const updatedMap = new Map(filesMap);
    let removed = false;
    const removedFiles: string[] = []; // Track which files were removed

    backendFiles.forEach((backendFile) => {
      const backendFileLike = backendFile.file as any;
      const backendDatasetId =
        backendFile.datasetId ?? backendFileLike?.datasetId;
      const backendSdrId =
        backendFile.sourceDataRecordId ?? backendFileLike?.sourceDataRecordId;
      const backendName = backendFile.file?.name?.toLowerCase();

      Array.from(updatedMap.entries()).forEach(([fileId, fileState]) => {
        if (!locallyAddedFiles.current.has(fileId)) {
          return;
        }

        const fileLike = fileState.file as any;
        const localDatasetId = fileState.datasetId ?? fileLike?.datasetId;
        const localSdrId =
          fileState.sourceDataRecordId ?? fileLike?.sourceDataRecordId;
        const localName = fileState.file?.name?.toLowerCase();

        const matchesBySdr =
          backendSdrId !== undefined &&
          backendSdrId !== null &&
          localSdrId !== undefined &&
          localSdrId !== null &&
          backendSdrId === localSdrId;

        const matchesByDataset =
          backendDatasetId !== undefined &&
          backendDatasetId !== null &&
          localDatasetId !== undefined &&
          localDatasetId !== null &&
          backendDatasetId === localDatasetId &&
          backendName !== undefined &&
          backendName !== null &&
          backendName === localName;

        if (matchesBySdr || matchesByDataset) {
          removedFiles.push(`${fileState.file?.name} (${fileId})`);
          if (fileState.preview) {
            URL.revokeObjectURL(fileState.preview);
          }
          locallyAddedFiles.current.delete(fileId);
          removedDuringSync.current.add(fileId);
          const identity = getFileIdentity(fileState);
          fileIdentityMap.current.delete(identity);
          updatedMap.delete(fileId);
          removed = true;
        }
      });
    });

    return removed
      ? ({ map: updatedMap, removed: true } as const)
      : ({ map: filesMap, removed: false } as const);
  };

  // Debounced query invalidation to prevent multiple concurrent fetches
  const triggerQueryInvalidation = (fileId: string) => {
    if (!dialogueId) return;

    // Track this file as completed
    completedFileIds.current.add(fileId);

    // Clear existing timeout
    if (queryInvalidationTimeout.current) {
      clearTimeout(queryInvalidationTimeout.current);
    }

    // Set new timeout to batch multiple file completions
    queryInvalidationTimeout.current = setTimeout(() => {
      queryClient.invalidateQueries(["project", dialogueId]);

      // Clear the completed files set after invalidation
      completedFileIds.current.clear();
      queryInvalidationTimeout.current = null;
    }, 1000); // 1 second delay to ensure all files have time to complete
  };

  const [managerState, setManagerState] = useState<UploadManagerState>(() => {
    return {
      files: new Map(existingFiles.map((file) => [file.id, file])),
      activeUploads: 0,
      completedCount: existingFiles.filter(
        (f) => f.processingStatus === "completed",
      ).length,
      failedCount: existingFiles.filter(
        (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
      ).length,
      totalProgress: 0,
    };
  });
  const [isUploading, setIsUploading] = useState(false);

  // Initialize manager and update contextId when it changes
  useEffect(() => {
    if (!managerRef.current) {
      managerRef.current = new FileUploadManager();
    }
    return () => {
      managerRef.current?.cancelAll();
    };
  }, []);

  // Update manager contextId when it changes (for dialogue creation)
  useEffect(() => {
    if (
      managerRef.current &&
      contextId !== undefined &&
      contextId !== null &&
      contextId !== "temp"
    ) {
      // The manager will use the updated contextId in subsequent upload calls
    }
  }, [contextId]);

  // Calculate overall progress
  const calculateOverallProgress = (filesMap: Map<string, FileUploadState>) => {
    if (filesMap.size === 0) return 0;

    let totalProgress = 0;
    filesMap.forEach((file) => {
      // Each file has two phases: upload (50%) and processing (50%)
      const uploadProgress = (file.uploadProgress || 0) * 0.5;
      const processingProgress = (file.processingProgress || 0) * 0.5;
      totalProgress += uploadProgress + processingProgress;
    });

    return totalProgress / filesMap.size;
  };

  // Update file state
  const updateFileState = (fileState: FileUploadState) => {
    // Check if this file has been removed during sync - if so, don't re-add it
    if (removedDuringSync.current.has(fileState.id)) {
      return;
    }

    // Check if this file has been permanently removed by the user - if so, don't re-add it
    if (permanentlyRemovedFiles.current.has(fileState.id)) {
      return;
    }

    setFiles((prev) => {
      const newFiles = new Map(prev);
      newFiles.set(fileState.id, fileState);
      return newFiles;
    });

    setManagerState((prev) => {
      const newFiles = new Map(prev.files);
      newFiles.set(fileState.id, fileState);

      const completedCount = Array.from(newFiles.values()).filter(
        (f) => f.processingStatus === "completed",
      ).length;

      const failedCount = Array.from(newFiles.values()).filter(
        (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
      ).length;

      const activeUploads = Array.from(newFiles.values()).filter(
        (f) => f.uploadStatus === "uploading",
      ).length;

      return {
        files: newFiles,
        activeUploads,
        completedCount,
        failedCount,
        totalProgress: calculateOverallProgress(newFiles),
      };
    });
  };

  // Handle file upload progress
  const handleFileProgress = (fileState: FileUploadState) => {
    // Add datasetId and sourceDataRecordId to the actual File object so components can access it
    if (fileState.datasetId) {
      (fileState.file as FileWithPreview).datasetId = fileState.datasetId;
      (fileState as any).datasetId = fileState.datasetId;
    }
    if (fileState.sourceDataRecordId) {
      (fileState.file as FileWithPreview).sourceDataRecordId =
        fileState.sourceDataRecordId;
      (fileState as any).sourceDataRecordId = fileState.sourceDataRecordId;
    }

    updateFileState(fileState);

    // Update FileStatusStore to sync UI state
    const { updateFileStatus } = useFileStatusStore.getState();
    updateFileStatus(fileState.id, {
      id: fileState.id,
      name: fileState.file.name,
      // Backend-provided fields (if available)
      display_status: (fileState as any).display_status,
      is_processing: (fileState as any).is_processing,
      progress_percentage: (fileState as any).progress_percentage,
      status: (fileState as any).status,
      // Legacy fields for backward compatibility
      isUploading:
        fileState.uploadStatus === "uploading" ||
        (fileState.uploadStatus === "uploaded" &&
          fileState.processingStatus === "processing"),
      uploadProgress: fileState.uploadProgress,
      embeddingProgress: fileState.processingProgress,
    });

    // Call appropriate callbacks based on status
    if (
      fileState.uploadStatus === "uploading" &&
      fileState.uploadProgress === 0
    ) {
      onUploadStart?.(fileState);
    } else if (fileState.uploadStatus === "uploading") {
      onUploadProgress?.(fileState);
    } else if (fileState.uploadStatus === "uploaded") {
      onUploadComplete?.(fileState);
      if (
        fileState.processingStatus === "processing" &&
        fileState.processingProgress === 0
      ) {
        onProcessingStart?.(fileState);
      }
    } else if (fileState.processingStatus === "processing") {
      onProcessingProgress?.(fileState);
    }
  };

  // Handle file processing complete
  const handleFileComplete = (fileState: FileUploadState) => {
    updateFileState(fileState);
    onProcessingComplete?.(fileState);

    // Clear from upload tracking when complete
    uploadInitiated.current.delete(fileState.id);

    // Use debounced query invalidation when a file completes in dialogue context
    // This ensures useMergedDatasets will refetch updated project data with the new dataset
    if (
      context === "dialogue" &&
      dialogueId &&
      fileState.processingStatus === "completed" &&
      fileState.datasetId
    ) {
      triggerQueryInvalidation(fileState.id);
    }

    // Mark completed local files as pending backend sync
    // This prevents duplicates while keeping files visible during sync
    if (
      fileState.processingStatus === "completed" &&
      locallyAddedFiles.current.has(fileState.id) &&
      fileState.datasetId
    ) {
      const markedFile = {
        ...fileState,
        syncStatus: "pending-backend-sync" as const,
      };

      updateFileState(markedFile);

      // Set a timeout to prevent files from being stuck in sync state
      setTimeout(() => {
        setFiles((prev) => {
          const file = prev.get(fileState.id);
          if (file?.syncStatus === "pending-backend-sync") {
            const updatedFile = { ...file, syncStatus: "synced" as const };
            const newFiles = new Map(prev);
            newFiles.set(file.id, updatedFile);
            return newFiles;
          }
          return prev;
        });
      }, 30000); // 30 second timeout
    }

    // Trigger deduplication after file completion to handle multiple concurrent completions
    triggerDeduplication();

    // Check if all files are complete
    setFiles((prev) => {
      const allComplete = Array.from(prev.values()).every(
        (f) =>
          f.processingStatus === "completed" || f.processingStatus === "failed",
      );
      if (allComplete) {
        onAllComplete?.(Array.from(prev.values()));
        setIsUploading(false);
      }
      return prev;
    });
  };

  // // Handle file error
  // const handleFileError = (fileState: FileUploadState, error: string) => {
  //   // Check if it's a 409 conflict error
  //   const is409Conflict =
  //     error.toLowerCase().includes("conflict") ||
  //     error.includes("409") ||
  //     error.includes("already exists");

  //   if (is409Conflict) {
  //     // For 409 conflicts, remove the file entirely from the queue
  //     console.log(
  //       `❌ 409 Conflict detected for file ${fileState.file.name}. Removing from queue.`
  //     );

  //     // Remove the file from state immediately
  //     removeFile(fileState.id);

  //     // Call error callback with specific conflict message
  //     onError?.(
  //       fileState,
  //       `File upload cancelled: ${fileState.file.name} already exists or another upload is in progress. Please use a different filename or wait and try again.`
  //     );
  //   } else {
  //     // For other errors, update the file state but keep it in the list
  //     updateFileState(fileState);
  //     onError?.(fileState, error);
  //   }

  //   // Clear from upload tracking on any error
  //   uploadInitiated.current.delete(fileState.id);
  // };

  // Handle file error
  const handleFileError = (fileState: FileUploadState, error: string) => {
    // CRITICAL FIX: Mark file as failed to prevent re-upload loops
    const failedFileState = {
      ...fileState,
      uploadStatus: "failed" as const,
      processingStatus: "failed" as const,
      error: error,
    };

    console.error(`Upload error for file ${fileState.file.name}: ${error}`);

    // Update the file state to show error in UI
    updateFileState(failedFileState);

    // Call error callback
    onError?.(failedFileState, error);

    // Clear from upload tracking on any error
    uploadInitiated.current.delete(fileState.id);
  };

  // Add files to queue with support for duplicate files
  const addFiles = (acceptedFiles: File[]) => {
    // Check file count limit
    const currentFileCount = files.size;
    if (currentFileCount >= maxFiles) {
      const error = `Cannot upload files. Maximum ${maxFiles} files allowed. You have reached the limit.`;
      onError?.(
        {
          file: acceptedFiles[0],
          id: "limit-exceeded",
          uploadStatus: "failed",
          uploadProgress: 0,
          processingStatus: "failed",
          processingProgress: 0,
          error: error,
        },
        error,
      );
      return;
    }

    if (currentFileCount + acceptedFiles.length > maxFiles) {
      const availableSlots = maxFiles - currentFileCount;
      const error = `Cannot upload ${acceptedFiles.length} files. Maximum ${maxFiles} files allowed. You have ${availableSlots} files remaining.`;
      onError?.(
        {
          file: acceptedFiles[0],
          id: "limit-exceeded",
          uploadStatus: "failed",
          uploadProgress: 0,
          processingStatus: "failed",
          processingProgress: 0,
          error: error,
        },
        error,
      );
      return;
    }

    // Check for duplicate filenames in existing files
    const existingFileNames = new Set<string>();
    Array.from(files.values()).forEach((existingFile) => {
      existingFileNames.add(existingFile.file.name.toLowerCase());
    });

    // Filter out duplicate files and show error for each
    const duplicateFiles: File[] = [];
    const uniqueFiles: File[] = [];

    acceptedFiles.forEach((file) => {
      if (existingFileNames.has(file.name.toLowerCase())) {
        duplicateFiles.push(file);
      } else {
        uniqueFiles.push(file);
      }
    });

    // Show error for duplicate files
    if (duplicateFiles.length > 0) {
      duplicateFiles.forEach((file) => {
        const error = `File "${file.name}" already exists in this project. Please use a different filename or remove the existing file first.`;
        console.warn(`⚠️ Duplicate file detected: ${file.name}`);
        onError?.(
          {
            file,
            id: `duplicate-${file.name}-${Date.now()}`,
            uploadStatus: "failed",
            uploadProgress: 0,
            processingStatus: "failed",
            processingProgress: 0,
            error: error,
          },
          error,
        );
      });
    }

    // If all files were duplicates, return early
    if (uniqueFiles.length === 0) {
      return;
    }

    // Continue with only unique files
    // Create file states with improved unique ID generation
    const timestamp = Date.now();
    const newFileStates: FileUploadState[] = uniqueFiles.map((file, index) => {
      // Generate unique ID that includes timestamp and random component to handle duplicates
      const uniqueId = `${file.name}_${
        file.size
      }_${timestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`;

      return {
        file,
        id: uniqueId,
        uploadStatus: "pending",
        uploadProgress: 0,
        processingStatus: "pending",
        processingProgress: 0,
        preview: URL.createObjectURL(file),
        sizeFormatted: formatFileSize(file.size),
      };
    });

    // Add to state and track as locally added
    // Also update FileStatusStore for UI tracking
    const { updateFileStatus } = useFileStatusStore.getState();

    // Track all files as locally added first
    newFileStates.forEach((fileState) => {
      locallyAddedFiles.current.add(fileState.id);
      // Update FileStatusStore for UI tracking
      updateFileStatus(fileState.id, {
        id: fileState.id,
        name: fileState.file.name,
        isUploading: false,
        uploadProgress: 0,
        embeddingProgress: 0,
      });
    });

    // Batch all file additions into a single state update
    // This prevents N re-renders when adding N files
    setFiles((prev) => {
      const newFiles = new Map(prev);
      newFileStates.forEach((fileState) => {
        newFiles.set(fileState.id, fileState);
      });
      return newFiles;
    });

    // Also update manager state in a single batch
    setManagerState((prev) => {
      const newFiles = new Map(prev.files);
      newFileStates.forEach((fileState) => {
        newFiles.set(fileState.id, fileState);
      });
      return {
        ...prev,
        files: newFiles,
        totalProgress: calculateOverallProgress(newFiles),
      };
    });

    // Only notify about unique files that were actually added
    if (uniqueFiles.length > 0) {
      onFilesSelected?.(uniqueFiles);
    }

    // Note: Auto-upload will be handled in a separate effect to avoid circular dependency
  };

  // Start upload process
  const startUpload = async (
    filesToUpload?: FileUploadState[],
    forceContextId?: string | number,
  ) => {
    const effectiveContextId = forceContextId || getEffectiveContextId();

    if (
      !managerRef.current ||
      effectiveContextId === undefined ||
      effectiveContextId === null ||
      effectiveContextId === "temp"
    ) {
      return;
    }

    const uploadFiles =
      filesToUpload ||
      Array.from(files.values()).filter((f) => f.uploadStatus === "pending");

    if (uploadFiles.length === 0) {
      return;
    }

    setIsUploading(true);

    try {
      await managerRef.current.uploadMultipleFiles(
        context,
        effectiveContextId,
        uploadFiles,
        uploadConcurrency,
        handleFileProgress,
        handleFileComplete,
        handleFileError,
        chunkSize,
      );
    } catch (error) {
      setIsUploading(false);
    }
  };

  // Remove file
  const removeFile = async (fileId: string) => {
    const file = files.get(fileId);
    if (!file) return;

    // Cancel any ongoing operations
    managerRef.current?.cancelFile(fileId);

    // Mark as permanently removed BEFORE any async operations.
    // This prevents polling/processing callbacks (and re-renders triggered by
    // adding new files) from re-adding this file to the UI during the async gap.
    permanentlyRemovedFiles.current.add(fileId);

    // Clear from upload tracking
    uploadInitiated.current.delete(fileId);
    locallyAddedFiles.current.delete(fileId);

    // Revoke object URL if exists
    if (file.preview) {
      URL.revokeObjectURL(file.preview);
    }

    // Remove from state IMMEDIATELY so onFilesChange fires with the correct
    // (post-removal) file list before any async operations run.
    setFiles((prev) => {
      const newFiles = new Map(prev);
      newFiles.delete(fileId);
      return newFiles;
    });

    setManagerState((prev) => {
      const newFiles = new Map(prev.files);
      newFiles.delete(fileId);
      return {
        ...prev,
        files: newFiles,
        totalProgress: calculateOverallProgress(newFiles),
      };
    });

    // Track if deletion was successful for cache invalidation
    let deletionSuccessful = false;

    // Dialogue context: workspace-direct uploads — delete from workspace
    if (context === "dialogue" && file.workspacePath) {
      try {
        await axios.delete(
          `${nuclearn_api_url}/workspace/files/${file.workspacePath}`,
        );
        deletionSuccessful = true;
      } catch (error) {
        console.error(
          `Workspace file delete failed: ${file.workspacePath}`,
          error,
        );
      }
    }

    // Get the SDR ID from the file state or the typed file object (set by FileUploadManager)
    let sourceDataRecordId =
      file.sourceDataRecordId ||
      (file.file as FileWithPreview).sourceDataRecordId;

    // A project file without an SDR id is looked up by exact filename only;
    // deleting any other row's SDR would remove a different document.
    if (
      !deletionSuccessful &&
      !sourceDataRecordId &&
      file.datasetId &&
      context === "project"
    ) {
      try {
        const response = await axios.get(
          `${nuclearn_api_url}/datasets/processing-status?dataset_ids=${file.datasetId}`,
        );
        sourceDataRecordId = selectStatusRow(response.data ?? [], {
          filename: file.file.name,
        })?.source_data_record_id;
      } catch (error) {
        console.error("Failed to fetch SDR ID from processing status:", error);
      }
    }

    // With no SDR id, nothing is deleted on the server: the project's upload
    // dataset is shared by every file in the project.
    if (!deletionSuccessful && sourceDataRecordId) {
      try {
        await axios.delete(
          `${nuclearn_api_url}/datasets/delete-sdr-by-id/${sourceDataRecordId}`,
        );
        deletionSuccessful = true;
      } catch (error: any) {
        console.error(
          `❌ SDR DELETE FAILED >>> SDR ID: ${sourceDataRecordId} | Error:`,
          error.response?.data || error.message,
        );
        // Don't prevent local removal even if backend deletion fails
        // This allows the user to clean up their UI even if the backend is unavailable
      }
    }

    // Invalidate project cache after successful deletion in project context
    if (deletionSuccessful && context === "project") {
      const projectId = getEffectiveContextId();
      if (projectId) {
        // Invalidate all project-related queries to ensure fresh data
        queryClient.invalidateQueries(["project", projectId]);
        queryClient.invalidateQueries(["projects"]);
        queryClient.invalidateQueries(["dialogue"]); // In case dialogues cache project data

        // Also invalidate any datasets queries that might be cached
        if (file.datasetId) {
          queryClient.invalidateQueries(["dataset", file.datasetId]);
        }
      }
    }

    // For dialogue context, invalidate workspace files cache so file explorer refreshes
    if (deletionSuccessful && context === "dialogue") {
      queryClient.invalidateQueries(["workspace-files"]);
      if (dialogueId) {
        queryClient.invalidateQueries(["project", dialogueId]);
      }
    }

    onRemoveFile?.(file);
  };

  // Cancel all uploads
  const cancelAll = () => {
    managerRef.current?.cancelAll();
    setIsUploading(false);
  };

  // Helper function to show temporary validation error
  const showValidationError = (message: string) => {
    setValidationError(message);

    // Clear existing timeout
    if (validationErrorTimeout.current) {
      clearTimeout(validationErrorTimeout.current);
    }

    // Auto-clear error after 5 seconds
    validationErrorTimeout.current = setTimeout(() => {
      setValidationError(null);
      validationErrorTimeout.current = null;
    }, 5000);
  };

  // Clear validation error manually
  const clearValidationError = () => {
    setValidationError(null);
    if (validationErrorTimeout.current) {
      clearTimeout(validationErrorTimeout.current);
      validationErrorTimeout.current = null;
    }
  };

  // MIME entries in acceptedFileTypes (image/png) read as extensions (.png).
  const acceptedTypesLabel = () =>
    acceptedFileTypes
      ?.map((t) => (t.includes("/") ? `.${t.split("/")[1]}` : t))
      .join(", ") || "supported formats";

  // Handle file rejections from dropzone
  const handleFileRejections = (rejectedFiles: any[]) => {
    if (rejectedFiles.length === 0) return;

    // Group rejected files by error type for better error messaging
    const errorGroups = {
      tooLarge: [] as string[],
      invalidType: [] as string[],
      tooMany: [] as string[],
      other: [] as { file: string; message: string }[],
    };

    rejectedFiles.forEach((rejection) => {
      const fileName = rejection.file.name;
      const errors = rejection.errors;

      if (errors.some((e: any) => e.code === "file-too-large")) {
        errorGroups.tooLarge.push(fileName);
      } else if (errors.some((e: any) => e.code === "file-invalid-type")) {
        errorGroups.invalidType.push(fileName);
      } else if (errors.some((e: any) => e.code === "too-many-files")) {
        errorGroups.tooMany.push(fileName);
      } else {
        errorGroups.other.push({
          file: fileName,
          message: errors.map((e: any) => e.message).join(", "),
        });
      }
    });

    // Build consolidated error message
    const errorParts: string[] = [];
    const totalRejected = rejectedFiles.length;

    if (totalRejected === 1) {
      // Single file - use original format
      const rejection = rejectedFiles[0];
      const file = rejection.file;
      const errors = rejection.errors;

      let singleErrorMessage = `File "${file.name}" was rejected: `;

      if (errors.some((e: any) => e.code === "file-too-large")) {
        if (maxFileSize) {
          const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
          singleErrorMessage += `File size exceeds ${maxSizeMB}MB limit.`;
        } else {
          singleErrorMessage += `File size exceeds the limit.`;
        }
      } else if (errors.some((e: any) => e.code === "file-invalid-type")) {
        singleErrorMessage += `File type not supported. Please upload: ${acceptedTypesLabel()}`;
      } else if (errors.some((e: any) => e.code === "too-many-files")) {
        singleErrorMessage += `Too many files selected. Maximum ${maxFiles} files allowed.`;
      } else {
        singleErrorMessage += errors.map((e: any) => e.message).join(", ");
      }

      showValidationError(singleErrorMessage);
      return;
    }

    // Multiple files - use grouped format
    let errorMessage = `${totalRejected} files were rejected:\n`;

    if (errorGroups.tooLarge.length > 0) {
      if (maxFileSize) {
        const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
        errorParts.push(
          `• File size exceeds ${maxSizeMB}MB limit: ${errorGroups.tooLarge.join(
            ", ",
          )}`,
        );
      } else {
        errorParts.push(
          `• File size exceeds the limit: ${errorGroups.tooLarge.join(", ")}`,
        );
      }
    }

    if (errorGroups.invalidType.length > 0) {
      errorParts.push(
        `• File type not supported (please upload: ${acceptedTypesLabel()}): ${errorGroups.invalidType.join(
          ", ",
        )}`,
      );
    }

    if (errorGroups.tooMany.length > 0) {
      errorParts.push(
        `• Too many files selected (maximum ${maxFiles} files allowed): ${errorGroups.tooMany.join(
          ", ",
        )}`,
      );
    }

    if (errorGroups.other.length > 0) {
      errorGroups.other.forEach(({ file, message }) => {
        errorParts.push(`• ${file}: ${message}`);
      });
    }

    errorMessage += errorParts.join("\n");

    showValidationError(errorMessage);
  };

  // Retry failed uploads
  const retryFailed = () => {
    const failedFiles = Array.from(files.values()).filter(
      (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
    );

    // Reset their status to pending
    failedFiles.forEach((file) => {
      updateFileState({
        ...file,
        uploadStatus: "pending",
        uploadProgress: 0,
        processingStatus: "pending",
        processingProgress: 0,
        error: undefined,
      });
    });

    // Start upload
    startUpload(failedFiles);
  };

  // Setup dropzone
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      const toUpload = interceptFiles
        ? interceptFiles(acceptedFiles)
        : acceptedFiles;
      if (toUpload.length > 0) addFiles(toUpload);
    },
    onDropRejected: handleFileRejections,
    accept: acceptedFileTypes ? acceptedFileTypes.join(",") : undefined,
    maxSize: maxFileSize,
    multiple: allowMultiple,
    noClick: true,
    noKeyboard: true,
    preventDropOnDocument: true,
    // Chrome's showOpenFilePicker keeps only MIME entries from `accept`, so
    // extension lists get no filter there. The plain input honours both.
    useFsAccessApi: false,
  });

  // Handle updates to existingFiles prop (e.g., after project data refresh)
  // Use a separate effect with minimal dependencies to prevent infinite loops
  const existingFilesLength = existingFiles.length;
  const existingFilesIds = existingFiles
    .map((f) => f.id)
    .sort()
    .join(",");

  useEffect(() => {
    // If existingFiles is empty, don't process but also don't mark as complete
    // This allows dialogue history files to load when they arrive later
    if (existingFilesLength === 0) {
      return;
    }

    // Only sync on initial mount or when coming from a genuine external source
    // After initial mount, ignore updates to prevent feedback loops from onFilesChange
    if (hasCompletedInitialMount.current) {
      return;
    }

    // Check if we've already processed this exact set of files
    if (processedExistingFiles.current.has(existingFilesIds)) {
      return;
    }

    // Mark this set as being processed
    processedExistingFiles.current.add(existingFilesIds);

    // Only do the synchronization, avoid any async operations that call updateFileState
    setFiles((prev) => {
      let currentFiles = new Map(prev);
      const existingFileIds = new Set(existingFiles.map((f) => f.id));
      let hasChanges = false;

      // Add any new existing files that aren't already tracked
      existingFiles.forEach((file) => {
        if (!currentFiles.has(file.id)) {
          currentFiles.set(file.id, file);
          hasChanges = true;
        }
      });

      // Remove files that are no longer in existingFiles (but keep locally added files)
      // NOTE: This removal logic only runs when existingFiles has content
      Array.from(currentFiles.keys()).forEach((fileId) => {
        const file = currentFiles.get(fileId);
        if (
          !existingFileIds.has(fileId) &&
          file?.datasetId &&
          !locallyAddedFiles.current.has(fileId) &&
          file?.uploadStatus !== "uploading" &&
          file?.processingStatus !== "processing"
        ) {
          currentFiles.delete(fileId);
          hasChanges = true;

          if (file?.preview) {
            URL.revokeObjectURL(file.preview);
          }
        }
      });

      const { map: dedupedFiles, removed: removedDuplicates } =
        removeLocalDuplicates(currentFiles, existingFiles);

      if (removedDuplicates) {
        currentFiles = dedupedFiles;
        hasChanges = true;
      }

      if (!hasChanges) {
        return prev;
      }

      const snapshot = new Map(currentFiles);
      const snapshotValues = Array.from(snapshot.values());
      const completedCount = snapshotValues.filter(
        (f) => f.processingStatus === "completed",
      ).length;
      const failedCount = snapshotValues.filter(
        (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
      ).length;
      const activeUploads = snapshotValues.filter(
        (f) => f.uploadStatus === "uploading",
      ).length;

      setManagerState({
        files: snapshot,
        activeUploads,
        completedCount,
        failedCount,
        totalProgress: calculateOverallProgress(snapshot),
      });

      return currentFiles;
    });

    // Mark initial mount as complete after processing
    hasCompletedInitialMount.current = true;
  }, [existingFilesLength, existingFilesIds]); // Use stable string-based dependency

  // Track which files have been checked for deduplication to prevent infinite loops
  const deduplicatedFiles = useRef<Set<string>>(new Set());

  // Note: Deduplication is now only triggered when files complete uploading
  // via the triggerDeduplication() function called in handleFileComplete()
  // This prevents infinite re-rendering from watching file state changes

  // Check existing files' processing status on mount
  useEffect(() => {
    const checkExistingFilesStatus = async () => {
      if (!managerRef.current || existingFiles.length === 0) return;

      for (const file of existingFiles) {
        // Only check files that have a datasetId and are not already completed
        if (file.datasetId && file.processingStatus !== "completed") {
          try {
            const status = await managerRef.current.checkProcessingStatus({
              sdrId: file.sourceDataRecordId,
              datasetId: file.datasetId,
              filename: file.file.name,
            });

            const updatedFile = {
              ...file,
              processingStatus: status.isComplete
                ? ("completed" as const)
                : ("processing" as const),
              processingProgress: status.processingProgress,
              numSdr: status.numSdr,
              numSdrNotEmbedded: status.numSdrNotEmbedded,
            };

            updateFileState(updatedFile);

            // If file is not complete and needs processing, start polling
            if (!status.isComplete && status.numSdr > 0) {
              managerRef.current
                .pollProcessingStatus(
                  updatedFile,
                  handleFileProgress,
                  handleFileComplete,
                )
                .catch((error) => {
                  const errorMessage =
                    error instanceof Error
                      ? error.message
                      : "Processing failed";
                  handleFileError(updatedFile, errorMessage);
                });
            } else if (status.isComplete) {
              // Trigger completion callback for completed files
              handleFileComplete(updatedFile);
            }
          } catch (error) {
            // Error checking status for existing file
          }
        }
      }
    };

    checkExistingFilesStatus();
  }, [existingFiles.length]); // Only depend on length to avoid re-running when file states change

  // Track upload initiation to prevent multiple triggers
  const uploadInitiated = useRef<Set<string>>(new Set());

  // Auto-upload effect - triggers when contextId becomes valid and there are pending files
  useEffect(() => {
    if (autoStartUpload) {
      const effectiveContextId = getEffectiveContextId();
      if (effectiveContextId && effectiveContextId !== "temp") {
        const pendingFiles = Array.from(files.values()).filter(
          (f) => f.uploadStatus === "pending",
        );

        // Only start upload for files that haven't been initiated yet
        const newFilesToUpload = pendingFiles.filter(
          (f) => !uploadInitiated.current.has(f.id),
        );

        if (newFilesToUpload.length > 0) {
          // Mark files as initiated
          newFilesToUpload.forEach((f) => uploadInitiated.current.add(f.id));

          startUpload(newFilesToUpload);
        }
      }
    }
  }, [
    autoStartUpload,
    dynamicContextId,
    files,
    startUpload,
    getEffectiveContextId,
  ]); // Add all dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  // Reset all files and internal state
  const resetFiles = () => {
    // Cancel all ongoing uploads
    managerRef.current?.cancelAll();

    // Clean up object URLs for all files with previews
    files.forEach((file) => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });

    // Clear all internal state
    setFiles(new Map());
    setIsUploading(false);
    setManagerState({
      files: new Map(),
      activeUploads: 0,
      completedCount: 0,
      failedCount: 0,
      totalProgress: 0,
    });

    // Clear all tracking refs
    locallyAddedFiles.current.clear();
    processedExistingFiles.current.clear();
    removedDuringSync.current.clear();
    uploadInitiated.current.clear();
    completedFileIds.current.clear();
    deduplicatedFiles.current.clear();

    // Clear timeouts
    if (queryInvalidationTimeout.current) {
      clearTimeout(queryInvalidationTimeout.current);
      queryInvalidationTimeout.current = null;
    }

    // Reset sync locks
    syncLock.current.isProcessingBackendSync = false;
    syncLock.current.isDeduplicating = false;

    // Clear file identity tracking
    fileIdentityMap.current.clear();
    permanentlyRemovedFiles.current.clear();
  };

  return {
    // State
    files: Array.from(files.values()),
    filesMap: files,
    managerState,
    isUploading,
    isDragActive,
    validationError, // Expose validation error state

    // Actions
    addFiles,
    startUpload,
    removeFile,
    cancelAll,
    retryFailed,
    resetFiles, // Add the new reset method
    openFileDialog: open,
    updateContextId, // Expose for dialogue creation
    resetContextId, // Drop a runtime-assigned id on conversation reset
    clearValidationError, // Expose clear validation error function

    // Dropzone props
    getRootProps,
    getInputProps,

    // Computed values
    hasFiles: files.size > 0,
    hasPendingFiles: Array.from(files.values()).some(
      (f) => f.uploadStatus === "pending",
    ),
    hasFailedFiles: Array.from(files.values()).some(
      (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
    ),
    totalProgress: managerState.totalProgress,
  };
};
